import React from 'react'
import { Card, Row, Col, Button, OverlayTrigger, Tooltip, Placeholder } from 'react-bootstrap'

import { auctionBudgetCardLabels, campaignAlerts } from '@revbox/localization'
import { useCookiesContext } from '../../../contexts/CookiesContext'
import { OverlayContainer } from '../../../services/currency/RenderTooltip'
import { useEurUsdRate } from '../../../hooks/useEurUsdRate'

function roundUpToHundred(amount) {
    if (typeof amount !== 'number') {
        return 0
    }

    if (Number(amount) === 0) {
        return 0
    }

    if (Number(amount) < 0) {
        return 0
    }

    if (Number(amount) === Infinity) {
        return 0
    }

    if (Number(amount) === -Infinity) {
        return 0
    }

    if (isNaN(Number(amount))) {
        return 0
    }

    return Math.ceil(Number(amount) / 100) * 100
}

export default function DefaultAuctionCreditBalanceCard(props) {
    const { getLanguageAbbr } = useCookiesContext()
    const languageAbbr = getLanguageAbbr()
    const multilang = auctionBudgetCardLabels[languageAbbr]
    const alerts = campaignAlerts[languageAbbr]
    const { campaign, budgetLoading, handleShowBottom, days_diff, isIframe = false, id } = props
    const { rate } = useEurUsdRate();
    const ppc_monthly_budget = campaign?.ppc_monthly_budget ? parseFloat(campaign?.ppc_monthly_budget?.replace(/,/g, '')) : 0
    const ppc_monthly_spend = campaign?.ppc_monthly_spend ? parseFloat(campaign?.ppc_monthly_spend?.replace(/,/g, '')) : 0
    const spendPercentage = Math.round((ppc_monthly_spend / ppc_monthly_budget) * 100)
    const forecastSpendPercentage = 100 - spendPercentage > 0 ? 100 - spendPercentage : 0

    const isBudgetLowAlert = campaign?.forecast_spend_persentage >= 110 && !campaign?.ppc_paused && campaign?.forecast_spend_amount > 0
    const isBudgetUtilizationAlert =
        campaign?.forecast_spend_persentage <= 85 &&
        campaign?.forecast_spend_persentage > 0 &&
        !campaign?.ppc_paused &&
        campaign?.forecast_spend_amount > 0
    const isBudgetOnTrack =
        campaign?.forecast_spend_persentage < 110 &&
        campaign?.forecast_spend_persentage > 85 &&
        !campaign?.ppc_paused &&
        campaign?.forecast_spend_amount > 0
    const isCampaignPaused = campaign?.ppc_paused && campaign?.forecast_spend_amount > 0
    const isAnyAlert = isBudgetLowAlert || isBudgetUtilizationAlert

    const forecastSpendAmount = campaign?.forecast_spend_amount ? parseFloat(campaign?.forecast_spend_amount) : 0
    const forecastSpendDays = Math.floor((ppc_monthly_budget - ppc_monthly_spend) / ((forecastSpendAmount - ppc_monthly_spend) / days_diff))

    const today = new Date()
    const spendDate = new Date(today.setDate(today.getDate() + forecastSpendDays))

    if (!campaign || budgetLoading) {
        return (
            <React.Fragment>
                <Placeholder as={Card.Title} animation="glow" style={{ color: 'var(--bs-body-color)' }}>
                    <Placeholder style={{ height: 145, width: '100%' }} />
                </Placeholder>
            </React.Fragment>
        )
    }

    const cardContent = (
        <Card className={`card-one cursor-pointer ${isIframe ? 'iframe-card-background' : ''}`} onClick={campaign?.ppc_active ? handleShowBottom : null} id={id}>
            <Card.Body>
                <Row>
                    <Col xs="12">
                        <h3 className="card-value mb-0 flex justify-content-between">
                            <OverlayContainer amount={campaign?.ppc_monthly_budget} rate={rate}>
                                <b>${campaign?.ppc_monthly_budget}</b>
                            </OverlayContainer>
                            {!isIframe && (
                                <Button variant="outline-primary" size="sm" disabled={!campaign?.ppc_active}>
                                    {multilang.button}
                                </Button>
                            )}
                        </h3>
                        <label className="card-title fw-medium text-dark mb-1">{multilang.title}</label>
                    </Col>
                </Row>
            </Card.Body>
            <Card.Footer style={{ height: '60px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <Row>
                    <Col xs="12">
                        <div className="revenue-item">
                            <div className="d-flex justify-content-between revenue-item-body mb-0 mt-1">
                                <span className="d-block fs-11 text-secondary" style={{ maxWidth: 'max-content' }}>
                                    <OverlayContainer amount={campaign?.ppc_monthly_spend} rate={rate}>
                                        <span className="m-0">${campaign?.ppc_monthly_spend}</span>
                                    </OverlayContainer>
                                    {` ${multilang.spent}`}
                                </span>
                                <span className="d-block fs-11 text-secondary" style={{ width: 'max-content' }}>
                                    {+days_diff === 1 ? multilang.resets.tomorrow : multilang.resets.inDays(days_diff)}
                                </span>
                            </div>
                            <div className="progress mt-1" style={{ height: '6px' }}>
                                {spendPercentage > 100 ? (
                                    <div
                                        role="progressbar"
                                        className="progress-bar bg-danger"
                                        aria-valuenow="70"
                                        aria-valuemin="0"
                                        aria-valuemax="100"
                                        style={{ width: spendPercentage + '%' }}
                                    ></div>
                                ) : spendPercentage > 75 ? (
                                    <div
                                        role="progressbar"
                                        className="progress-bar bg-warning"
                                        aria-valuenow="70"
                                        aria-valuemin="0"
                                        aria-valuemax="100"
                                        style={{ width: spendPercentage + '%' }}
                                    ></div>
                                ) : (
                                    <div
                                        role="progressbar"
                                        className="progress-bar bg-success"
                                        aria-valuenow="70"
                                        aria-valuemin="0"
                                        aria-valuemax="100"
                                        style={{ width: spendPercentage + '%' }}
                                    ></div>
                                )}
                            </div>
                        </div>
                        {isAnyAlert && (
                            <div className="d-flex justify-content-between mt-1">
                                <span className="d-block fs-11 text-secondary">
                                    <div className="d-flex text-danger justify-content-center items-align-center">
                                        {(isBudgetLowAlert || isBudgetUtilizationAlert) && (
                                            <div className="me-1">
                                                <i class="ri-alert-fill"></i>
                                            </div>
                                        )}
                                        <div>
                                            {isBudgetLowAlert ? alerts.low_budget.title : isBudgetUtilizationAlert ? alerts.budget_utilization.title : ''}
                                        </div>
                                    </div>
                                </span>
                            </div>
                        )}
                    </Col>
                </Row>
            </Card.Footer>
        </Card>
    )

    if (isIframe) {
        return cardContent
    }

    return (
        <OverlayTrigger
            placement="bottom"
            overlay={
                <Tooltip placement="bottom">
                    {isBudgetOnTrack ? (
                        <div className="d-flex flex-column justify-content-start">
                            <div>
                                <b>{multilang.tooltip.onTrack.title}</b>
                            </div>
                            <div>{multilang.tooltip.onTrack.message(Number(campaign?.forecast_spend_persentage).toFixed(0))}</div>
                        </div>
                    ) : isBudgetLowAlert ? (
                        <div className="d-flex flex-column justify-content-start">
                            <div>
                                <b>{multilang.tooltip.lowBudget.title}</b>
                            </div>
                            <div>
                                {multilang.tooltip.lowBudget.message(
                                    spendDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric' }),
                                    roundUpToHundred(campaign?.forecast_spend_amount).toFixed(0),
                                    rate
                                )}
                            </div>
                        </div>
                    ) : isBudgetUtilizationAlert ? (
                        <div className="d-flex flex-column justify-content-start">
                            <div>
                                <b>{multilang.tooltip.underDeliver.title}</b>
                            </div>
                            <div>{multilang.tooltip.underDeliver.message(Number(campaign?.forecast_spend_persentage).toFixed(0))}</div>
                        </div>
                    ) : isCampaignPaused ? (
                        <div className="d-flex flex-column justify-content-start">
                            <div>
                                <b>{multilang.tooltip.paused.title}</b>
                            </div>
                            <div>{multilang.tooltip.paused.message(roundUpToHundred(campaign?.forecast_spend_amount).toFixed(0), rate)}</div>
                        </div>
                    ) : (
                        <div className="d-flex flex-column justify-content-start">
                            <div>
                                <b>{campaign?.ppc_paused ? multilang.tooltip.generic.paused : multilang.tooltip.generic.bidding}</b>
                            </div>
                        </div>
                    )}
                </Tooltip>
            }
        >
            {cardContent}
        </OverlayTrigger>
    )
}
